import {
  Avatar,
  Button,
  ProgressBar,
  Tooltip
} from "@etframework/ui.components";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useUpdateTaskCollaborators } from "../../data/mutations/taskMutations";
import { useDueDatesAuth } from "../../hooks/useDueDatesAuth";
import useEvent from "../../hooks/useEvent";
import { CollaboratorMultiSelect } from "../CreateTaskModalComponents/CollaboratorMultiSelect";

export const TaskCollaboratorsContainer = ({
  label,
  boardPermissions,
  task
}) => {
  const elementRef = useRef(null);
  const collaboratorsRef = useRef(null);

  const { t } = useTranslation(["Overlay"]);
  const [usersDropDown, showUsersDropDown] = useState(false);
  const [loading, setLoading] = useState(false);

  const { UserId } = useDueDatesAuth();

  //Mutations
  const { mutate: updateTaskCollaborators } = useUpdateTaskCollaborators();

  const queryClient = useQueryClient();
  const { Events, toggleEvent } = useEvent();

  const handleChange = () => {
    const collaborators = collaboratorsRef.current
      ?.getValue()
      .map((x) => ({ Id: x }));

    if (checkIfHasChanges(collaborators, task?.Collaborators)) {
      updateCollaborators([
        ...(collaborators ?? []),
        ...(task?.Collaborators.map((x) => ({ Id: x.UserId })) ?? []),
      ]);
    }
  };

  const handleDeleteCollaborator = (e, collaborator) => {
    const collaborators = task?.Collaborators.filter(
      (x) => x.UserId !== collaborator.UserId
    );

    updateCollaborators(collaborators.map((x) => ({ Id: x.UserId })));
  };

  const updateCollaborators = (collaborators) => {
    setLoading(true);
    const collaboratorsUpdated = {
      TaskId: task.Id,
      CurrentUserId: UserId,
      Collaborators: collaborators,
    };

    updateTaskCollaborators(
      { collaborators: collaboratorsUpdated },
      {
        onSuccess: (response) => {
          queryClient.invalidateQueries(["get-task", task.Id]);
          toggleEvent(Events.RefreshTaskGrid);
          toast.success(response.Message, { position: "bottom-left" });
          showUsersDropDown(false);
          setTimeout(() => {
            setLoading(false);
          }, 2500);
        },
        onError: () => {},
      }
    );
  };

  //Checking if two array of objects have the same Ids so it does not update.
  const checkIfHasChanges = (array1, array2) => {
    const ids1 = array1.map((item) => item.Id).sort((a, b) => a - b);
    const ids2 = array2.map((item) => item.UserId).sort((a, b) => a - b);

    return JSON.stringify(ids1) !== JSON.stringify(ids2);
  };

  useEffect(() => {
    if (collaboratorsRef.current && usersDropDown) {
      collaboratorsRef.current?.getComponentRef().showPopup();
      collaboratorsRef.current?.getComponentRef().focusIn();
    }
  }, [usersDropDown]);

  return (
    <div ref={elementRef} className="collaborators-details">
      {loading ? (
        <div className="p-5">
          <div className="text-center">
            {t("CollaboratorsContainer.Updating")}
          </div>
          <ProgressBar
            text="Updating"
            id="linear"
            height="10"
            value={100}
            animation={{ enable: true, duration: 2000 }}
            progressColor="#000000"
          ></ProgressBar>
        </div>
      ) : (
        <div className="px-4 py-3">
          <div className="d-flex justify-content-between align-items-center">
            <div className="fw-600 fs-14 et-black">{label}</div>
            <div className="add-people">
              <Button
                text={t("CollaboratorsContainer.AddPeople")}
                className="btn-tertiary w-auto add-people-btn"
                onClick={() => {
                  showUsersDropDown((prev) => !prev);
                }}
                iconCss="fa-regular fa-plus"
              />
              <i className="plus-icon fa-regular fa-plus et-gray" />
            </div>
          </div>
          {usersDropDown && (
            <>
              <CollaboratorMultiSelect
                id={"collaborators-multiselect"}
                ref={collaboratorsRef}
                boardPermissions={boardPermissions?.Entities.filter(
                  (item) =>
                    !task?.Collaborators.some((a) => a.UserId === item.Id)
                )}
                placeholder={t("CollaboratorsContainer.SearchUsers")}
                onEnterKey={handleChange}
              />
              <div className="d-flex justify-content-end gap-2 my-2">
                <button
                  id={"confirm-collaborators"}
                  className="btn btn-default w-auto et-green-dark confirm-btn"
                  onClick={handleChange}
                >
                  <i className="fa-regular fa-check"></i>
                </button>
                <button
                  id={"cancel-collaborators"}
                  className="btn btn-default w-auto et-dark-red cancel-btn"
                  onClick={() => {
                    showUsersDropDown((prev) => !prev);
                  }}
                >
                  <i className="fa-regular fa-xmark-large"></i>
                </button>
              </div>
            </>
          )}
          {task?.Collaborators?.length > 0 ? (
            <Tooltip
              className={"d-inline"}
              content={() => {
                return (
                  <p className="p-1 rounded">
                    {t("CollaboratorsContainer.DeleteCollaborator")}
                  </p>
                );
              }}
              position="TopCenter"
              showTipPointer={false}
              target={".delete-collaborator-button"}
              windowCollision={true}
            >
            <div className="collaborators my-1">
              {task?.Collaborators.map((collaborator, index) => {
                return (
                  <div
                    key={index}
                    className="d-flex justify-content-between align-items-center gap-2 px-1 py-2 collaborator"
                  >
                    <div className="d-flex justify-content-start align-items-center gap-2">
                      <Avatar content={collaborator.FullName} />
                      <span>{collaborator.FullName}</span>
                    </div>
                      <i
                        className="fa-regular fa-xmark et-blue mx-2 cursor-pointer delete-collaborator-button"
                        onClick={(e) => {
                          handleDeleteCollaborator(e, collaborator);
                        }}
                      />
                  </div>
                );
              })}
            </div>
            </Tooltip>
          ) : (
            <span className="et-darkest-gray mx-2">
              {t("CollaboratorsContainer.None")}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
